import { Box, Heading, Text } from "@chakra-ui/react"
import { MethodI18nFragment } from "components/I18nText"
import { Logo } from "components/Logo"
import { useRating } from "lib/providers/RatingProvider"
import { FreeVersionWatermark, Page, SectionHeading } from "../Report/components"
import { DimensionSection } from "./components/DimensionContent"
import { Frontpage } from "./components/Frontpage"

const TOC = () => {
  const { method } = useRating()

  if (!method) return null

  return (
    <Page>
      <FreeVersionWatermark />
      <SectionHeading>Innholdsfortegnelse</SectionHeading>
      <ol className="toc-list">
        <li>
          <a href="#intro">
            <span className="title">Innledning fra ledelsen</span>
          </a>
        </li>
        <li>
          <a href="#about">
            <span className="title">Om rapporteringen Indikatorer</span>
          </a>
        </li>
        {method.config.kpiDimensions.map((dimension) => (
          <li key={dimension.id}>
            <a href={`#dimension-${dimension.slug}`}>
              <span className="title">
                <MethodI18nFragment text={dimension.name} />
              </span>
            </a>
            <ul>
              {dimension.categories.map((category) => (
                <li key={category.id}>
                  <a href={`#category-${category.id}`}>
                    <MethodI18nFragment text={category.name} />
                  </a>
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ol>
    </Page>
  )
}

const Intro: React.FC = () => {
  return (
    <Page id="intro">
      <Heading mb={"6mm"} fontWeight={400} fontSize={"18pt"}>
        Innledning fra ledelsen
      </Heading>
      <Text mb="4mm" fontWeight="400">
        Bærekraft blir stadig viktigere for hvordan virksomheter drives, styres og vurderes. For oss handler
        bærekraftsarbeid ikke bare om rapportering, men om å få bedre innsikt i egen drift og identifisere konkrete
        forbedringsområder.
      </Text>
      <Text mb="4mm" fontWeight="400">
        Denne rapporten er et første steg i å strukturere og synliggjøre sentrale indikatorer innen miljø, sosiale
        forhold og virksomhetsstyring. Gjennom arbeidet med rapporten har vi fått et bedre grunnlag for å forstå vår
        påvirkning, samt identifisere risikoer og muligheter.
      </Text>
      <Text mb="4mm" fontWeight="400">
        Ambisjonen er at rapporteringen skal fungere som et styringsverktøy – ikke bare som dokumentasjon. Innsikten fra
        rapporten vil derfor brukes aktivt i videre arbeid.
      </Text>
      <Text mb="4mm" fontWeight="400">
        Dette er vår første rapport basert på ESG Trackr Basic Module. Over tid vil vi videreutvikle rapporteringen og
        inkludere flere datapunkter.
      </Text>

      <Heading id="about" mt="12mm" mb={"6mm"} fontWeight={400} fontSize={"18pt"}>
        Om rapporten
      </Heading>
      <Text mb="4mm" fontWeight="400">
        Denne rapporten er utarbeidet ved bruk av ESG Trackr – Basic Module, og gir en strukturert oversikt over
        sentrale bærekraftsindikatorer innen miljø (E), sosiale forhold (S) og virksomhetsstyring (G). Rapporten er
        tilpasset VSME-standarden for små og mellomstore virksomheter.
      </Text>
      <Heading fontSize={"12pt"}>Hvordan lese rapporten</Heading>
      <Text mb="4mm" fontWeight="400">
        Rapporten er strukturert etter de tre hovedområdene E, S og G. For hvert område presenteres nøkkeltall som gir
        innsikt i virksomhetens nåsituasjon, etterfulgt av korte forklaringer som setter tallene i kontekst.
      </Text>
      <Text mb="4mm" fontWeight="400">
        Tallene bør forstås som indikatorer som kan bidra til å identifisere styrker, risikoer og forbedringsområder.
        Formålet er ikke kun å dokumentere status, men å gi et grunnlag for videre utvikling og bedre beslutninger.
      </Text>
      <Heading fontSize={"12pt"}>Datagrunnlag</Heading>
      <Text mb="4mm" fontWeight="400">
        Opplysningene er basert på tilgjengelige interne data for rapporteringsperioden, herunder regnskapsdata, HR-data
        og annen relevant informasjon. Rapporten er ikke gjenstand for ekstern verifikasjon.
      </Text>
      <Heading fontSize={"12pt"}>Videre utvikling</Heading>
      <Text mb="4mm" fontWeight="400">
        Dette er et første steg i virksomhetens bærekraftsrapportering. Over tid vil rapporten videreutvikles og kunne
        inkludere flere indikatorer.
      </Text>
    </Page>
  )
}

const FreeOutro: React.FC = () => {
  return (
    <Page lastPage>
      <Box>
        <Logo />
      </Box>
      <Heading mt={"80mm"} mb={"8mm"} fontWeight={300} fontSize={"15pt"} textAlign={"center"}>
        GRATIS -{" "}
        <Text as="span" fontWeight={600}>
          IKKE VERTIFISERT OG UFULLSTENDIG BÆREKRAFTSRAPPORT
        </Text>
      </Heading>
      <Box mt={"12mm"} mb="8mm" textAlign={"center"}>
        Tusen takk for at du tested Gratis versjonen av ESG Trackr AS sin bærekraftsrapport.
      </Box>
      <Box mb="8mm" textAlign={"center"}>
        Ved ønsker om yttligere informasjon, oppgradering av rapport eller bistand
        <br />
        ta kontakt med ESG Trackr AS på <a href="mailto:post@esgtrackr.no">post@esgtrackr.no</a>
      </Box>
    </Page>
  )
}

export const VSMEReport = () => {
  const { isReady, isFree, method } = useRating()

  if (!isReady || !method) return null

  return (
    <>
      <Frontpage />
      <TOC />
      <Intro />
      {method.config.kpiDimensions.map((dimension) => (
        <DimensionSection
          key={dimension.id}
          dimension={dimension}
          lastPage={!isFree && dimension.slug === "governance"}
        />
      ))}
      {isFree && <FreeOutro />}
    </>
  )
}
